import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getAccessToken } from './getAccessToken';
import { getDefaultStore } from 'jotai';
import { authStateAtom } from '@/entities/auth/model/auth.atom';

const EXTERNAL_API_URL = process.env.NEXT_PUBLIC_EXTERNAL_API_URL;

/**
 * 외부 API 호출용 axios 인스턴스
 * 401 에러 발생 시 자동으로 토큰 리프레시 후 재시도
 */
export const externalApiClient = axios.create({
  baseURL: EXTERNAL_API_URL,
  timeout: 30000,
  withCredentials: true,
});

// 토큰 리프레시 중인지 추적 (중복 리프레시 방지)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor: 요청 전에 토큰을 헤더에 추가
externalApiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor: 401 에러 발생 시 토큰 리프레시 후 재시도
externalApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 에러이고 아직 재시도하지 않은 요청인 경우
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      if (isRefreshing) {
        // 이미 리프레시 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers && token) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return externalApiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // BFF의 refresh API 호출 (lazy import to avoid circular dependency)
        const { authApi } = await import('@/shared/api/authApi');
        const refreshResponse = await authApi.refreshToken();

        if (refreshResponse.success) {
          const session = await authApi.getSession();

          const store = getDefaultStore();
          store.set(authStateAtom, {
            accessToken: session.accessToken,
            user: session.user,
            isAuthenticated: true,
          });

          processQueue(null, session.accessToken);

          if (originalRequest.headers && session.accessToken) {
            originalRequest.headers.Authorization = `Bearer ${session.accessToken}`;
          }
          return externalApiClient(originalRequest);
        } else {
          // 리프레시 실패 시 인증 상태 초기화 및 로그인 페이지로 리다이렉트
          const store = getDefaultStore();
          store.set(authStateAtom, {
            accessToken: null,
            user: null,
            isAuthenticated: false,
          });
          processQueue(new Error(refreshResponse.error?.message || '토큰 갱신에 실패했습니다.'));

          // 클라이언트 사이드에서만 리다이렉트
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 리프레시 실패 시 인증 상태 초기화 및 로그인 페이지로 리다이렉트
        const store = getDefaultStore();
        store.set(authStateAtom, {
          accessToken: null,
          user: null,
          isAuthenticated: false,
        });
        processQueue(refreshError as Error);

        // 클라이언트 사이드에서만 리다이렉트
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

/**
 * BFF API 호출용 axios 인스턴스
 * 401 에러 발생 시 자동으로 토큰 리프레시 후 재시도
 */
export const bffApiClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  timeout: 10000,
  withCredentials: true,
});

// BFF API용 토큰 리프레시 중인지 추적
let isBffRefreshing = false;
let bffFailedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processBffQueue = (error: Error | null) => {
  bffFailedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  bffFailedQueue = [];
};

// BFF API Response interceptor: 401 에러 발생 시 토큰 리프레시 후 재시도
bffApiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // 401 에러이고 아직 재시도하지 않은 요청인 경우
    // 단, refresh API와 me API는 재시도하지 않음 (무한 루프 방지)
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/me') &&
      !originalRequest.url?.includes('/auth/session')
    ) {
      if (isBffRefreshing) {
        // 이미 리프레시 중이면 큐에 추가
        return new Promise((resolve, reject) => {
          bffFailedQueue.push({ resolve, reject });
        })
          .then(() => {
            return bffApiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isBffRefreshing = true;

      try {
        // BFF의 refresh API 호출 (lazy import to avoid circular dependency)
        const { authApi } = await import('@/shared/api/authApi');
        const refreshResponse = await authApi.refreshToken();

        if (refreshResponse.success) {
          const session = await authApi.getSession();

          const store = getDefaultStore();
          store.set(authStateAtom, {
            accessToken: session.accessToken,
            user: session.user,
            isAuthenticated: true,
          });

          processBffQueue(null);

          return bffApiClient(originalRequest);
        } else {
          // 리프레시 실패 시 인증 상태 초기화 및 로그인 페이지로 리다이렉트
          const store = getDefaultStore();
          store.set(authStateAtom, {
            accessToken: null,
            user: null,
            isAuthenticated: false,
          });
          processBffQueue(new Error(refreshResponse.error?.message || '토큰 갱신에 실패했습니다.'));

          // 클라이언트 사이드에서만 리다이렉트
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // 리프레시 실패 시 인증 상태 초기화 및 로그인 페이지로 리다이렉트
        const store = getDefaultStore();
        store.set(authStateAtom, {
          accessToken: null,
          user: null,
          isAuthenticated: false,
        });
        processBffQueue(refreshError as Error);

        // 클라이언트 사이드에서만 리다이렉트
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      } finally {
        isBffRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);
